import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AdjustmentsHorizontalIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { LoginForm } from '../login/LoginForm';
import { transcriptApi } from '../../api/transcriptApi';
import { formatDuration } from '../../utils/formatDuration';
import './sessions.css';

const INITIAL_SESSIONS_LIMIT = 100;

const getField = (item, ...keys) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== '') {
      return item[key];
    }
  }
  return undefined;
};

const normalizeStatus = (status) => String(status || 'UNKNOWN').toUpperCase();

const getStatusClass = (status) => {
  switch (normalizeStatus(status)) {
    case 'COMPLETED':
      return 'completed';
    case 'PUBLISHED':
      return 'published';
    case 'PROCESSING':
    case 'IN_PROGRESS':
    case 'PENDING':
      return 'live';
    case 'FAILED':
    case 'CANCELLED':
      return 'danger';
    default:
      return 'neutral';
  }
};

const getSessionStatus = (session) => normalizeStatus(getField(
  session,
  'status',
  'transcript_status',
  'transcriptStatus'
));

const getSessionDate = (session) => getField(
  session,
  'updated_at',
  'updatedAt',
  'created_at',
  'createdAt',
  'processing_started_at',
  'processingStartedAt'
);

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const getSessionId = (session) => getField(session, 'id', 'session_id', 'sessionId');
const getTitle = (session) => getField(session, 'title', 'name', 'original_filename', 'originalFilename') || 'Untitled session';
const getFileName = (session) => getField(session, 'original_filename', 'originalFilename', 'file_path', 'filePath', 'file_url', 'fileUrl');
const getEventId = (session) => getField(session, 'event_id', 'eventId');
const getEventTitle = (session) => getField(session, 'event_title', 'eventTitle');
const getUserName = (session) => getField(session, 'user_name', 'userName', 'user_email', 'userEmail');
const getModel = (session) => getField(session, 'model', 'transcript_model', 'transcriptModel', 'transcript_provider', 'transcriptProvider');
const getDuration = (session) => getField(session, 'duration_seconds', 'durationSeconds');

const isWithinDateRange = (value, from, to) => {
  if (!from && !to) return true;
  if (!value) return false;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;

  if (from) {
    const fromDate = new Date(`${from}T00:00:00`);
    if (date < fromDate) return false;
  }

  if (to) {
    const toDate = new Date(`${to}T23:59:59.999`);
    if (date > toDate) return false;
  }

  return true;
};

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const normalized = normalizeStatus(status);

  return (
    <span className={`sessions-badge sessions-badge-${getStatusClass(normalized)}`}>
      <span className="sessions-badge-dot" />
      {normalized.replaceAll('_', ' ')}
    </span>
  );
};

const SessionTable = ({ sessions, columns, wrapperRef }) => (
  <div className="sessions-table-wrap" ref={wrapperRef}>
    <table className="sessions-table">
      <thead>
        <tr>
          <th>Title</th>
          {columns.status && <th>Status</th>}
          {columns.event && <th>Event</th>}
          {columns.user && <th>User</th>}
          {columns.model && <th>Model</th>}
          {columns.duration && <th>Duration</th>}
          {columns.updated && <th>Updated</th>}
        </tr>
      </thead>
      <tbody>
        {sessions.map((session) => {
          const sessionId = getSessionId(session);
          const title = getTitle(session);
          const fileName = getFileName(session);
          const eventId = getEventId(session);
          const eventTitle = getEventTitle(session);
          const userName = getUserName(session);
          const model = getModel(session);
          const duration = getDuration(session);
          const updated = getSessionDate(session);
          const statusValue = getField(session, 'status', 'transcript_status', 'transcriptStatus');

          return (
            <tr key={sessionId}>
              <td>
                <Link to={`/sessions/${sessionId}`} className="sessions-table-title">{title}</Link>
                {fileName && <div className="sessions-table-sub">{fileName}</div>}
              </td>
              {columns.status && <td><StatusBadge status={statusValue} /></td>}
              {columns.event && (
                <td>
                  {eventTitle ? (
                    eventId ? <Link to={`/events/${eventId}`} className="sessions-table-link">{eventTitle}</Link> : <span>{eventTitle}</span>
                  ) : '—'}
                </td>
              )}
              {columns.user && <td>{userName || '—'}</td>}
              {columns.model && <td>{model || '—'}</td>}
              {columns.duration && <td>{duration !== undefined ? formatDuration(duration) : '—'}</td>}
              {columns.updated && <td>{updated ? formatDateTime(updated) : '—'}</td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default function SessionsPage() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [eventFilter, setEventFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [modelFilter, setModelFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState('newest');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [perPage, setPerPage] = useState(9);
  const contentRef = useRef(null);
  const listRef = useRef(null);
  const paginationRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    const loadSessions = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await transcriptApi.getSessions({
          per_page: INITIAL_SESSIONS_LIMIT,
          compact: true,
          include_total: false,
        });
        const list = Array.isArray(data?.sessions)
          ? data.sessions
          : (Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []));
        setSessions(list);
      } catch (err) {
        console.error('Failed to load sessions:', err);
        setError('Failed to load sessions. Please try again later.');
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };

    loadSessions();
  }, [user]);

  const statusOptions = useMemo(() => {
    const statuses = sessions
      .map(session => getSessionStatus(session))
      .filter(value => value && value !== 'UNKNOWN')
      .filter((value, index, arr) => arr.indexOf(value) === index)
      .sort();

    return ['all', ...statuses];
  }, [sessions]);

  const eventOptions = useMemo(() => {
    const map = new Map();
    sessions.forEach((session) => {
      const id = getEventId(session);
      const title = getEventTitle(session);
      if (id && title) map.set(String(id), title);
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [sessions]);

  const userOptions = useMemo(() => {
    const values = sessions
      .map(session => getUserName(session))
      .filter(Boolean)
      .filter((value, index, arr) => arr.indexOf(value) === index)
      .sort();
    return values;
  }, [sessions]);

  const modelOptions = useMemo(() => {
    const values = sessions
      .map(session => getModel(session))
      .filter(Boolean)
      .filter((value, index, arr) => arr.indexOf(value) === index)
      .sort();
    return values;
  }, [sessions]);

  const filteredSessions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const selectedStatus = status.toUpperCase();

    const filtered = sessions.filter(session => {
      const sessionStatus = getSessionStatus(session);
      if (selectedStatus !== 'ALL' && sessionStatus !== selectedStatus) return false;
      if (eventFilter !== 'all' && String(getEventId(session)) !== eventFilter) return false;
      if (userFilter !== 'all' && getUserName(session) !== userFilter) return false;
      if (modelFilter !== 'all' && getModel(session) !== modelFilter) return false;
      if (!isWithinDateRange(getSessionDate(session), dateFrom, dateTo)) return false;
      if (!q) return true;

      const haystack = [
        getTitle(session),
        getFileName(session),
        getEventTitle(session),
        getUserName(session),
        getModel(session),
      ].filter(Boolean).join(' ').toLowerCase();

      return haystack.includes(q);
    });

    filtered.sort((a, b) => {
      if (sort === 'oldest') return new Date(getSessionDate(a) || 0) - new Date(getSessionDate(b) || 0);
      if (sort === 'az') {
        return String(getTitle(a)).localeCompare(String(getTitle(b)));
      }
      if (sort === 'duration') {
        return Number(getDuration(b) || 0) - Number(getDuration(a) || 0);
      }
      return new Date(getSessionDate(b) || 0) - new Date(getSessionDate(a) || 0);
    });

    return filtered;
  }, [dateFrom, dateTo, eventFilter, modelFilter, query, sessions, sort, status, userFilter]);

  const visibleColumns = useMemo(() => ({
    status: filteredSessions.some(session => getField(session, 'status', 'transcript_status', 'transcriptStatus')),
    event: filteredSessions.some(session => getEventTitle(session)),
    user: filteredSessions.some(session => getUserName(session)),
    model: filteredSessions.some(session => getModel(session)),
    duration: filteredSessions.some(session => getDuration(session) !== undefined),
    updated: filteredSessions.some(session => getSessionDate(session)),
  }), [filteredSessions]);

  const totalPages = Math.max(1, Math.ceil(filteredSessions.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filteredSessions.slice((currentPage - 1) * perPage, currentPage * perPage);
  const activeFilterCount = [
    status !== 'all',
    eventFilter !== 'all',
    userFilter !== 'all',
    modelFilter !== 'all',
    Boolean(dateFrom),
    Boolean(dateTo),
  ].filter(Boolean).length;

  useLayoutEffect(() => {
    if (loading || error || filteredSessions.length === 0) return undefined;

    const measurePageSize = () => {
      const content = contentRef.current;
      const list = listRef.current;
      if (!content || !list) return;

      const contentRect = content.getBoundingClientRect();
      const listRect = list.getBoundingClientRect();
      const paginationHeight = paginationRef.current?.getBoundingClientRect().height || 0;
      const paginationGap = paginationHeight ? parseFloat(getComputedStyle(paginationRef.current).paddingTop || '0') : 0;
      const availableHeight = contentRect.bottom - listRect.top - paginationHeight - paginationGap;

      if (availableHeight <= 0) return;

      const header = list.querySelector('thead');
      const row = list.querySelector('tbody tr');
      if (!header || !row) return;

      const headerHeight = header.getBoundingClientRect().height;
      const rowHeight = row.getBoundingClientRect().height;
      if (!rowHeight) return;

      const nextPageSize = Math.max(1, Math.floor((availableHeight - headerHeight) / rowHeight));
      setPerPage(current => (current === nextPageSize ? current : nextPageSize));
    };

    measurePageSize();
    window.addEventListener('resize', measurePageSize);
    return () => window.removeEventListener('resize', measurePageSize);
  }, [error, filteredSessions.length, loading, pageItems.length, visibleColumns]);

  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, eventFilter, modelFilter, query, sort, status, userFilter]);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const goToPage = (nextPage) => {
    const parsedPage = Number(nextPage);
    if (!Number.isFinite(parsedPage)) return;
    const safePage = Math.min(Math.max(Math.trunc(parsedPage), 1), totalPages);
    setPage(safePage);
  };

  const handlePageSubmit = (event) => {
    event.preventDefault();
    goToPage(pageInput);
  };

  useEffect(() => {
    if (!filtersOpen) return undefined;
    const handleClick = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setFiltersOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [filtersOpen]);

  const resetFilters = () => {
    setStatus('all');
    setEventFilter('all');
    setUserFilter('all');
    setModelFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  if (!user) {
    return <LoginForm />;
  }

  return (
    <div className="sessions-page">
      <div className="sessions-content" ref={contentRef}>
        <div className="sessions-page-head">
          <div>
            <h1>Sessions</h1>
            <p>{filteredSessions.length} of {sessions.length} sessions</p>
          </div>
        </div>

        <div className="sessions-toolbar">
          <div className="sessions-input sessions-search">
            <MagnifyingGlassIcon />
            <input
              placeholder="Search sessions, files, events, users..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          <div className="sessions-toolbar-spacer" />

          <div className="sessions-filter-wrap" ref={popoverRef}>
            <button
              type="button"
              className={`sessions-btn ${activeFilterCount ? 'active' : ''}`}
              onClick={() => setFiltersOpen(open => !open)}
            >
              <AdjustmentsHorizontalIcon />
              Filters
              {activeFilterCount > 0 && <span className="sessions-filter-count">{activeFilterCount}</span>}
            </button>

            {filtersOpen && (
              <div className="sessions-popover">
                <div className="sessions-popover-head">
                  <strong>Filters</strong>
                  {activeFilterCount > 0 && <button type="button" onClick={resetFilters}>Reset</button>}
                </div>

                <label>
                  <span>Status</span>
                  <select value={status} onChange={(event) => setStatus(event.target.value)}>
                    {statusOptions.map(option => (
                      <option key={option} value={option}>
                        {option === 'all' ? 'Any status' : option.replaceAll('_', ' ')}
                      </option>
                    ))}
                  </select>
                </label>

                {eventOptions.length > 0 && (
                  <label>
                    <span>Event</span>
                    <select value={eventFilter} onChange={(event) => setEventFilter(event.target.value)}>
                      <option value="all">Any event</option>
                      {eventOptions.map(([id, title]) => <option key={id} value={id}>{title}</option>)}
                    </select>
                  </label>
                )}

                {userOptions.length > 0 && (
                  <label>
                    <span>User</span>
                    <select value={userFilter} onChange={(event) => setUserFilter(event.target.value)}>
                      <option value="all">Any user</option>
                      {userOptions.map(userName => <option key={userName} value={userName}>{userName}</option>)}
                    </select>
                  </label>
                )}

                {modelOptions.length > 0 && (
                  <label>
                    <span>Model</span>
                    <select value={modelFilter} onChange={(event) => setModelFilter(event.target.value)}>
                      <option value="all">Any model</option>
                      {modelOptions.map(model => <option key={model} value={model}>{model}</option>)}
                    </select>
                  </label>
                )}

                <label className="sessions-date-field">
                  <span>Date from</span>
                  <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
                </label>

                <label className="sessions-date-field">
                  <span>Date to</span>
                  <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
                </label>
              </div>
            )}
          </div>

          <select className="sessions-input" value={sort} onChange={(event) => setSort(event.target.value)}>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="az">A to Z</option>
            <option value="duration">Longest</option>
          </select>

        </div>

        {loading ? (
          <div className="sessions-empty sessions-card-shell">
            <div className="sessions-spinner" />
            <div>
              <strong>Loading sessions...</strong>
              <span>Fetching session list from the backend.</span>
            </div>
          </div>
        ) : error ? (
          <div className="sessions-empty sessions-card-shell sessions-error">
            <DocumentTextIcon />
            <div>
              <strong>Sessions unavailable</strong>
              <span>{error}</span>
            </div>
          </div>
        ) : pageItems.length === 0 ? (
          <div className="sessions-empty sessions-card-shell">
            <DocumentTextIcon />
            <div>
              <strong>No sessions match.</strong>
              <span>Clear search or try another status.</span>
            </div>
          </div>
        ) : (
          <SessionTable sessions={pageItems} columns={visibleColumns} wrapperRef={listRef} />
        )}

        {!loading && !error && pageItems.length > 0 && (
          <div className="sessions-pagination" ref={paginationRef}>
            <span>{(currentPage - 1) * perPage + 1}-{Math.min(currentPage * perPage, filteredSessions.length)} of {filteredSessions.length}</span>
            <div className="sessions-pagination-controls">
              <button disabled={currentPage === 1} onClick={() => goToPage(1)}>
                First
              </button>
              <button disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)} aria-label="Previous page">
                <ChevronLeftIcon />
              </button>

              <form className="sessions-page-jump" onSubmit={handlePageSubmit}>
                <span>Page</span>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={pageInput}
                  onChange={(event) => setPageInput(event.target.value)}
                  onBlur={() => setPageInput(String(currentPage))}
                />
                <span>of {totalPages}</span>
              </form>

              <button disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)} aria-label="Next page">
                <ChevronRightIcon />
              </button>
              <button disabled={currentPage === totalPages} onClick={() => goToPage(totalPages)}>
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
